import { useState, useContext } from "react";
import {
  Typography,
  Box,
  Container,
  CircularProgress,
  Snackbar,
  Alert,
  Paper,
  Stack,
  Grid
} from "@mui/material";
import API from "../../api/api";
import { useNavigate, Navigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
// import { fetchIP } from "../../utils/fetchIP";
import { registerNotificationToken } from "../../notifications/notification";

import { GoogleLogin } from '@react-oauth/google';

import RestaurantMenuRoundedIcon from '@mui/icons-material/RestaurantMenuRounded';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import SoupKitchenOutlinedIcon from '@mui/icons-material/SoupKitchenOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';

import logoImage from "../../assets/mealno-brand-logo.svg";
import scooterImg from "../../assets/scooter.png";
import foodLeftImg from "../../assets/food-left.png";
import foodRightImg from "../../assets/food-right.png";
import leaf1Img from "../../assets/leaf-1.png";
import leaf2Img from "../../assets/leaf-2.png";
import skylineImg from "../../assets/skyline.png";
import pinLeftImg from "../../assets/location-pin-left.png";
import pinRightImg from "../../assets/location-pin-right.png";
import patternImg from "../../assets/pattern.png";

export default function LoginPage() {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user, login } = useContext(AuthContext);
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };

  if (user) {
    return <Navigate to="/home" replace={true} />;
  }

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#ffffff",
        position: "relative",
        justifyContent: "flex-start",
      }}
    >
      {/* Background Pattern */}
      <Box sx={{ position: 'absolute', inset: 0, backgroundImage: `url(${patternImg})`, backgroundSize: '150px', opacity: 0.04, zIndex: 0, pointerEvents: 'none' }} />
      
      {/* Absolute Foreground Decorations */}
      <Box component="img" src={foodLeftImg} alt="Food Left" sx={{ position: 'absolute', top: -10, left: -50, width: {xs: 110, sm: 140}, zIndex: 0, pointerEvents: 'none' }} />
      <Box component="img" src={foodRightImg} alt="Food Right" sx={{ position: 'absolute', top: 10, right: -40, width: {xs: 90, sm: 120}, zIndex: 0, pointerEvents: 'none' }} />
      <Box component="img" src={leaf1Img} alt="Leaf" sx={{ position: 'absolute', top: '15%', left: '20%', width: 18, zIndex: 0, pointerEvents: 'none', transform: 'rotate(-20deg)' }} />
      <Box component="img" src={leaf2Img} alt="Leaf" sx={{ position: 'absolute', top: '5%', right: '12%', width: 24, zIndex: 0, pointerEvents: 'none', transform: 'rotate(15deg)' }} />      
      <Box component="img" src={leaf1Img} alt="Leaf" sx={{ position: 'absolute', top: '28%', left: '8%', width: 16, zIndex: 0, pointerEvents: 'none', transform: 'rotate(60deg)' }} />
      
      {/* Header Section */}
      <Box sx={{ pt: "clamp(32px, 8vh, 80px)", pb: 0, textAlign: "center", zIndex: 2, px: 2, flexShrink: 0 }}>
        <img src={logoImage} alt="Mealno Logo" style={{ height: "42px", width: "auto", marginBottom: "8px"}} />
        <Typography sx={{ fontSize: "1.05rem", fontWeight: 700, color: "#3A2C23", lineHeight: 1.3 }}>
          Authentic Flavors,<br />
          <span style={{ color: "#6A9E3A" }}>Delivered Fast</span>
        </Typography>
      </Box>

      {/* Middle Flexible Area */}
      <Box sx={{ 
        flexGrow: 1, 
        position: 'relative', 
        display: 'flex', 
        flexDirection: 'column', 
        justifyContent: 'flex-end',
        minHeight: '15vh'
      }}>
        {/* Skyline */}
        <Box component="img" src={skylineImg} alt="Skyline" sx={{ 
          position: 'absolute', 
          bottom: '0px', 
          left: 0, 
          right: 0, 
          width: '100%', 
          height: 'clamp(180px, 32vh, 300px)', 
          opacity: 0.15, 
          zIndex: 0, 
          pointerEvents: 'none', 
          objectFit: 'cover', 
          objectPosition: 'bottom center'
        }} />

        {/* Map Pins */}
        <Box component="img" src={pinLeftImg} alt="Pin" sx={{ position: 'absolute', bottom: 'clamp(100px, 16vh, 180px)', left: '15%', width: {xs: 28, sm: 32}, zIndex: 1, pointerEvents: 'none' }} />
        <Box component="img" src={pinRightImg} alt="Pin" sx={{ position: 'absolute', bottom: 'clamp(120px, 18vh, 210px)', right: '15%', width: {xs: 28, sm: 32}, zIndex: 1, pointerEvents: 'none' }} />

        {/* Scooter Graphic with Under-Wheel Shadow */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'flex-end', 
          zIndex: 4, 
          flexShrink: 0,
          position: 'relative',
          transform: 'translateY(16%)'
        }}>
          {/* Custom elliptical shadow strictly under the wheels */}
          <Box sx={{
            position: 'absolute',
            bottom: '17%', // 👈 Pushes the shadow safely UP so it doesn't touch the text
            width: '30%', // Slightly narrower
            height: '8px', // Thinner shadow
            bgcolor: 'rgba(58, 44, 35, 0.3)', // Slightly lighter
            borderRadius: '50%',
            filter: 'blur(6px)', // 👈 Tighter blur so it stops bleeding downward
            zIndex: 0
          }} />

          {/* Scooter Image */}
          <Box 
            component="img" 
            src={scooterImg} 
            alt="Delivery Boy" 
            sx={{ 
              maxHeight: 'clamp(180px, 28vh, 320px)',
              maxWidth: '85%', 
              width: 'auto',
              height: 'auto',
              display: 'block',
              position: 'relative',
              zIndex: 1 // Ensures scooter stays on top of the shadow
            }} 
          />
        </Box>
      </Box>

      {/* Main Login Card - Tucked behind the scooter */}
      <Container 
        maxWidth="xs" 
        sx={{ 
          zIndex: 3, 
          position: "relative", 
          flexShrink: 0,
          mt: '-13px' 
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, sm: 3 },
            borderRadius: "24px",
            bgcolor: "#FFFFFF",
            boxShadow: "0 12px 32px rgba(58, 44, 35, 0.08)",
            border: "1px solid #ECE5DA",
            width: "100%",
            boxSizing: "border-box"
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
            <Box sx={{ width: 46, height: 46, borderRadius: "50%", bgcolor: "#FFF6DE", display: "flex", justifyContent: "center", alignItems: "center", flexShrink: 0 }}>
              <RestaurantMenuRoundedIcon sx={{ color: "#F8C332", fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: "#3A2C23", lineHeight: 1.2, mb: 0.2 }}>
                Welcome to Mealno
              </Typography>
              <Typography sx={{ fontSize: "0.8rem", fontWeight: 500, color: "#A59D96", lineHeight: 1.3 }}>
                Healthy, delicious meals from<br />trusted home chefs.
              </Typography>
            </Box>
          </Stack>

          <Box sx={{ display: "flex", flexDirection: "column", width: "100%", mb: 3 }}>
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, height: '40px' }}>
                <CircularProgress size={24} thickness={5} sx={{ color: "#F8C332" }} />
                <Typography sx={{ color: "#6E655E", fontWeight: 600, fontSize: "0.9rem" }}>
                  Signing you in...
                </Typography>
              </Box>
            ) : (
              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                <GoogleLogin
                  onSuccess={async (credentialResponse) => {
                    setLoading(true);
                    try {
                      const res = await API.post("/auth/google", {
                        token: credentialResponse.credential,
                        frontend_ip: null // Backend handles this automatically now
                      });
                      if (res.data.exists) {
                        login(res.data);
                        navigate("/home");
                        registerNotificationToken(res.data.user.id, "USER", res.data.user.name).catch(console.error);
                      } else {
                        navigate("/register", { state: { prefilled: res.data.prefilled, googleToken: credentialResponse.credential } });
                      }
                    } catch (err) {
                      setSnackbar({ open: true, message: "Google authentication failed.", severity: "error" });
                    } finally {
                      setLoading(false);
                    }
                  }}
                  onError={() => {
                    setSnackbar({ open: true, message: "Google Sign-In Failed.", severity: "error" });
                  }}
                  shape="pill"
                  theme="outline" 
                  size="large"
                  text="signin_with"
                />
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', my: { xs: 2, sm: 3 } }}>
            <Box sx={{ flexGrow: 1, height: '1px', bgcolor: '#F2EEE8' }} />
            <Box sx={{ flexGrow: 1, height: '1px', bgcolor: '#F2EEE8' }} />
          </Box>

          <Grid container spacing={1} justifyContent="space-between">
            <Grid item xs={4}>
              <Stack alignItems="center" textAlign="center">
                <VerifiedUserOutlinedIcon sx={{ color: "#F8C332", fontSize: 26, mb: 0.5 }} />
                <Typography sx={{ fontSize: "0.7rem", fontWeight: 800, color: "#3A2C23" }}>Safe & Secure</Typography>
                <Typography sx={{ fontSize: "0.6rem", color: "#A59D96", mt: 0.2 }}>Your data is protected</Typography>
              </Stack>
            </Grid>
            <Grid item xs={4}>
              <Stack alignItems="center" textAlign="center">
                <SoupKitchenOutlinedIcon sx={{ color: "#F8C332", fontSize: 26, mb: 0.5 }} />
                <Typography sx={{ fontSize: "0.7rem", fontWeight: 800, color: "#3A2C23" }}>Home Cooked</Typography>
                <Typography sx={{ fontSize: "0.6rem", color: "#A59D96", mt: 0.2 }}>Made with love</Typography>
              </Stack>
            </Grid>
            <Grid item xs={4}>
              <Stack alignItems="center" textAlign="center">
                <TimerOutlinedIcon sx={{ color: "#F8C332", fontSize: 26, mb: 0.5 }} />
                <Typography sx={{ fontSize: "0.7rem", fontWeight: 800, color: "#3A2C23" }}>Super Fast</Typography>
                <Typography sx={{ fontSize: "0.6rem", color: "#A59D96", mt: 0.2 }}>Quick delivery</Typography>
              </Stack>
            </Grid>
          </Grid>
        </Paper>
      </Container>

      {/* Bottom Spacer */}
      <Box
        sx={{
          flexGrow: { xs: 0.1, sm: 0.4 },
          minHeight: "calc(clamp(60px, 12vh, 100px) + 2vh)", 
          maxHeight: "18vh",
          flexShrink: 0
        }}
      />

      {/* Yellow Footer */}
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "100%",
          height: "clamp(60px, 12vh, 100px)",
          bgcolor: "#F8D548",
          borderRadius: "50% 50% 0 0 / 100% 100% 0 0",
          zIndex: 1,
        }}
      />

      {/* Alert Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert 
          onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled" 
          sx={{ width: '100%', borderRadius: '16px', fontWeight: 700, bgcolor: snackbar.severity === 'error' ? '#E85D3F' : '#6A9E3A' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}